import { useTranslation } from '@doccareplus/shared/lib';
import { Button } from './Button';

/**
 * The branded recovery screen the boundary falls back to. Its own module so `ErrorBoundary.tsx` exports only the
 * boundary class — Fast Refresh cannot handle a module that mixes a class component with a function component.
 */
export function ErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation('common');

  return (
    <div
      role="alert"
      className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-[var(--bg)] px-6 select-none"
    >
      {/* 1. Medical grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(var(--border) 1px, transparent 1px),
            linear-gradient(90deg, var(--border) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
        }}
      />

      {/* 2. Danger-tinted radial aura */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[350px] w-[350px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-[var(--danger)]/10 via-[var(--danger)]/5 to-transparent blur-[80px] md:h-[600px] md:w-[600px] md:blur-[120px]" />

      {/* 3. Floating card */}
      <div className="relative z-10 flex w-full max-w-[460px] flex-col items-center rounded-[20px] border border-[var(--border)] bg-[var(--surface)] p-8 text-center shadow-[var(--shadow-3)] md:p-10 [animation:eb-pop-in_0.3s_ease-out]">
        {/* Animated monitor graphic — flatlining EKG */}
        <div className="relative mb-8 flex items-center justify-center">
          <div className="absolute h-[130px] w-[130px] animate-ping rounded-full border border-[var(--danger)]/20 opacity-70" />
          <div className="absolute h-[160px] w-[160px] rounded-full border border-[var(--danger)]/10 [animation:pulse_3s_infinite]" />

          <div className="relative flex h-[100px] w-[100px] items-center justify-center overflow-hidden rounded-full border border-[var(--border-strong)] bg-[var(--surface-2)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)]">
            <svg
              className="relative z-10 h-12 w-16 text-[var(--danger)]"
              viewBox="0 0 100 50"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden="true"
            >
              {/* A heartbeat that spikes then flatlines — reads as "something stopped". */}
              <path
                d="M 5 25 L 28 25 L 34 12 L 42 40 L 49 25 L 58 25 L 62 18 L 66 25 L 95 25"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: 200,
                  strokeDashoffset: 200,
                  animation: 'eb-ekg-sweep 2.6s linear infinite',
                }}
              />
            </svg>
          </div>
        </div>

        {/* Alert badge */}
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--danger-bg)] px-3 py-1 text-[11.5px] font-semibold uppercase tracking-[0.12em] text-[var(--danger)]">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
          Error
        </span>

        <h1 className="mt-4 text-[22px] font-bold tracking-tight text-[var(--text)] md:text-[26px]">
          {t('error.title', { defaultValue: 'Something went wrong' })}
        </h1>
        <p className="mt-3 max-w-[360px] text-[13px] leading-relaxed text-[var(--text-3)]">
          {t('error.description', {
            defaultValue:
              'An unexpected error interrupted this page. Your data is safe — try again, and if the problem persists, reload the app.',
          })}
        </p>

        {/* Collapsible technical detail — muted, opt-in */}
        {error.message && (
          <details className="mt-4 w-full text-left">
            <summary className="cursor-pointer text-[11.5px] font-semibold text-[var(--text-3)] transition-colors hover:text-[var(--text-2)]">
              {t('error.details', { defaultValue: 'Technical details' })}
            </summary>
            <pre className="mt-2 max-h-[140px] overflow-auto rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] p-3 text-[11px] leading-relaxed text-[var(--text-2)] whitespace-pre-wrap break-words">
              {error.message}
            </pre>
          </details>
        )}

        {/* Actions */}
        <div className="mt-8 flex w-full flex-col items-stretch gap-2.5 border-t border-[var(--border)] pt-6">
          <Button variant="primary" fullWidth onClick={reset} className="h-10 rounded-[9px] text-[13px] font-bold">
            {t('error.tryAgain', { defaultValue: 'Try again' })}
          </Button>
          <Button
            variant="outline"
            fullWidth
            onClick={() => window.location.reload()}
            className="h-10 rounded-[9px] text-[13px] font-semibold"
          >
            {t('error.reload', { defaultValue: 'Reload app' })}
          </Button>
        </div>
      </div>

      {/* Local keyframes — self-contained so the boundary never depends on app CSS. */}
      <style>{`
        @keyframes eb-ekg-sweep {
          0% { stroke-dashoffset: 200; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes eb-pop-in {
          0% { opacity: 0; transform: translateY(8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
